import React, { useEffect, useState } from "react";
import Layout from "../../Components/Navigation/Layout";
import Cards from "../../Components/Admin/Cards";
import LineChartComponent from "../../Components/Admin/LineChart";
import WelcomeCard from "../../Components/Admin/WelcomeCard";
import axios from "axios";
import SkeletonLoader from "../../Components/Loader";
import { FaChartPie, FaSignal, FaShieldAlt } from "react-icons/fa";

const Dashboard = () => {
  const [totalShops, setTotalShops]   = useState(0);
  const [activeShops, setActiveShops] = useState(0);
  const [userData, setUserData]       = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res  = await axios.get("/server/baos_function/dashboard");
        const data = res.data.data;
        setTotalShops(data.totalUsers);
        setActiveShops(data.activeUsers);
        setUserData(data.user);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <SkeletonLoader />
      </Layout>
    );
  }

  const inactiveShops = totalShops - activeShops;
  const activeRate    = totalShops > 0 ? Math.round((activeShops / totalShops) * 100) : 0;

  return (
    <Layout>
      <style>{`
        .dash-container {
          height: calc(100vh - 120px); 
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
          overflow: hidden; 
        }
        
        .dash-top-section {
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .dash-bottom-row {
          flex: 1;
          display: grid;
          grid-template-columns: 1fr 300px;
          gap: 16px;
          min-height: 0; 
          max-height: calc(100vh - 420px);
          align-items: stretch;
        }

        /* Advanced Summary panel */
        .dash-summary {
          background:#fff; border-radius:16px;
          border:1px solid #CBCBCB;
          padding:18px;
          box-shadow:0 10px 30px rgba(0,0,0,0.02);
          display:flex;
          flex-direction:column;
          height: 100%;
          overflow: hidden;
          position: relative;
        }
        .dash-summary::after {
          content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px;
          background: #931B5B; opacity: 0.8;
        }
        .dash-sum-hdr {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #F2F2F2;
        }
        .dash-sum-title { font-size: 14px; font-weight: 800; color: #0B2E33; display: flex; align-items: center; gap: 8px; }
        .dash-sum-icon { color: #931B5B; font-size: 14px; }

        .dash-sum-content {
          display: flex;
          flex-direction: column;
          gap: 10px;
          flex: 1;
        }
        .dash-sum-item {
          background: #fafafa; border-radius: 12px; padding: 10px 12px;
          border: 1px solid #F2F2F2; transition: all 0.3s ease;
        }
        .dash-sum-item:hover { border-color: #CBCBCB; transform: translateX(4px); background: #fff; }
        .dash-sum-row { display:flex; align-items:center; justify-content:space-between; gap:8px; }
        .dash-sum-lbl { font-size:11px; color:#6b7280; font-weight:600; display: flex; align-items: center; gap: 6px; }
        .dash-sum-val { font-size:14px; font-weight:800; color: #0B2E33; }

        .dash-bar-section { margin-top: 10px; padding: 12px; background: #0B2E33; border-radius: 12px; color: #fff; }
        .dash-bar-lbl { display:flex; justify-content:space-between; font-size:11px; color:rgba(184,227,233,0.7); margin-bottom:6px; }
        .dash-bar-track { height:6px; background:rgba(255,255,255,0.1); border-radius:99px; overflow:hidden; }
        .dash-bar-fill  {
          height:100%; border-radius:99px;
          background: linear-gradient(90deg, #B8E3E9, #4F7C82);
          box-shadow: 0 0 10px rgba(184,227,233,0.4);
          transition:width 1s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .dash-status-box {
          margin-top: auto; padding: 10px; border-radius: 10px; background: #F2F2F2;
          display: flex; align-items: center; gap: 10px;
        }
        .dash-status-pulse {
          width: 8px; height: 8px; background: #10b981; border-radius: 50%;
          position: relative;
        }
        .dash-status-pulse::after {
          content: ''; position: absolute; inset: -4px; border-radius: 50%;
          background: #10b981; opacity: 0.4;
          animation: status-pulse 2s infinite;
        }
        @keyframes status-pulse { 0%{transform:scale(1);opacity:0.4} 100%{transform:scale(2.5);opacity:0} }
        
        .dash-status-info { display: flex; flex-direction: column; }
        .dash-status-txt { font-size: 11px; font-weight: 700; color: #0B2E33; }
        .dash-status-sub { font-size: 9px; color: #9ca3af; }

        @media(max-width: 1000px) {
          .dash-container { height: auto; overflow: visible; }
          .dash-bottom-row { grid-template-columns: 1fr; height: auto; max-height: none; }
        }
      `}</style>

      <div className="dash-container">
        <div className="dash-top-section">
          <WelcomeCard />
          <Cards totalShops={totalShops} activeShops={activeShops} />
        </div>

        <div className="dash-bottom-row">
          <LineChartComponent userData={userData} />

          <div className="dash-summary">
            <div className="dash-sum-hdr">
              <div className="dash-sum-title">
                <FaChartPie className="dash-sum-icon" /> 📋 Quick Summary
              </div>
              <FaSignal style={{ color: "#CBCBCB", fontSize: 12 }} />
            </div>
            
            <div className="dash-sum-content">
              <div className="dash-sum-item">
                <div className="dash-sum-row">
                  <span className="dash-sum-lbl">Total Registered</span>
                  <span className="dash-sum-val">{totalShops}</span>
                </div>
              </div>
              <div className="dash-sum-item">
                <div className="dash-sum-row">
                  <span className="dash-sum-lbl" style={{ color: "#0B2E33" }}>● Active Now</span>
                  <span className="dash-sum-val" style={{ color: "#0B2E33" }}>{activeShops}</span>
                </div>
              </div>
              <div className="dash-sum-item">
                <div className="dash-sum-row">
                  <span className="dash-sum-lbl" style={{ color: "#931B5B" }}>○ Inactive</span>
                  <span className="dash-sum-val" style={{ color: "#931B5B" }}>{inactiveShops}</span>
                </div>
              </div>

              <div className="dash-bar-section">
                <div className="dash-bar-lbl">
                  <span>Performance Index</span>
                  <span style={{ color:"#B8E3E9", fontWeight:800 }}>{activeRate}%</span>
                </div>
                <div className="dash-bar-track">
                  <div className="dash-bar-fill" style={{ width:`${activeRate}%` }} />
                </div>
              </div>
            </div>

            <div className="dash-status-box">
              <div className="dash-status-pulse" />
              <div className="dash-status-info">
                <div className="dash-status-txt">System Secured</div>
                <div className="dash-status-sub">All nodes operational</div>
              </div>
              <FaShieldAlt style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 12 }} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
